import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CarMutateSliceModel } from "./carMutateSliceModel";
import { CarModel, CarResponseModel } from "../../domain/models/carModel";
import {
  GraphQLClient,
  GraphQLErrorMessage,
} from "../../../../shared/gql-client";
import { GET_CAR } from "../queries/carQueries";
import LicensePlate from "license-plate";
import { toast } from "sonner";
import i18next from "i18next";
import { CarTireSetResponseService } from "../service/carTireSetResponseService";
import CarService from "../service/carService";
import { GraphQLReadResponse } from "../../../general/domain/models/graphql";

const carAPI = new CarService();

export const getCar = createAsyncThunk(
  "car/mutate/get",
  async (id: string, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      const response = (await client.query("cars", GET_CAR, {
        id: parseInt(id),
      })) as GraphQLReadResponse;

      const carData = response.data[0] as CarResponseModel;
      const licencePlate = new LicensePlate(carData.licence || "");

      return CarTireSetResponseService.convertToCarModel(
        {
          id: carData.id,
          licencePlate: licencePlate.valid()
            ? licencePlate.pretty()
            : carData.licence,
          vin: carData.vin,
          additional_info: carData.additional_info,
          freestock_tire: carData.freestock_tire,
          label: carData.label,
          status: carData.status == 1 ? true : false,
          is_lease: carData.is_lease == 1 ? true : false,
          relationId: carData?.relation?.id ?? undefined,
          relationName: carData?.relation?.name ?? undefined,
          comment: carData.comment,
          customerId: carData?.customer?.id ?? undefined,
        },
        carData.tiresets,
      ) as CarModel;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const createCar = createAsyncThunk(
  "car/mutate/create",
  async (car: CarModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    carAPI.setClient(client);
    try {
      return await carAPI.mutate(car);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateCar = createAsyncThunk(
  "car/mutate/update",
  async (car: CarModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    carAPI.setClient(client);
    try {
      return await carAPI.mutate(car);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const deleteCar = createAsyncThunk(
  "car/mutate/delete",
  async (id: number, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    carAPI.setClient(client);
    try {
      return await carAPI.delete(id);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const initialState = {
  loading: true,
  car: undefined,
} as CarMutateSliceModel;

const carMutateSlice = createSlice({
  name: "carMutate",
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    removeTireset: (state, action) => {
      if (state.car && state.car.tireSets && state.car.tireSets.length > 0) {
        state.car.tireSets = state.car.tireSets.filter(
          (set) => set.id !== action.payload,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCar.fulfilled, (state, action) => {
      state.loading = false;
      state.car = action.payload as CarModel;
    });
    builder.addCase(createCar.fulfilled, (state, action) => {
      state.car = action.payload as CarModel;
      toast.success(
        i18next?.t("common:car.created", {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          label: action.payload.licencePlate || action.payload.label,
        }),
      );
    });
    builder.addCase(updateCar.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state.car = action.payload;
      toast.success(
        i18next?.t("common:car.updated", {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          label: action.payload.licencePlate || action.payload.label,
        }),
      );
    });
    builder.addCase(deleteCar.fulfilled, (_state, action) => {
      toast.error(
        i18next?.t("common:car.deleted", {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          label: action.payload.licencePlate || action.payload.label,
        }),
      );
    });
    builder.addMatcher(
      (action) =>
        [
          getCar.rejected.type,
          createCar.rejected.type,
          updateCar.rejected.type,
        ].includes(action.type),
      (state, action: { payload: GraphQLErrorMessage }) => {
        state.loading = false;
        console.error(action.payload);
        toast.error(action.payload.message);
      },
    );
  },
});

export const { clear, setLoading, removeTireset } = carMutateSlice.actions;

export default carMutateSlice.reducer;
